// Water FX: everything a cowboy does to water, in ONE draw call.
//
//   skim     a rooster tail of chunky spray behind a low flier, V wings of spray off the sides,
//            a lacy foam trail and Kelvin wake rings, and the jet pushing a churned dimple in
//            the surface behind the pack
//   hover    the nozzles blow a dimple straight down and whip up a circular spray corona and a
//            rolling mist ring, with ripple rings pulsing out
//   wade     ripple rings from the legs, a soft V wake of foam, a bow churn, a small splash per
//            stride
//   impact   a splash crown sized to the impact speed (upright sheet with spiky rim, lit like a
//            printed cel), a column on a dive, chunky droplets that rain back down and print
//            their own little rings where they land, mist, foam, expanding rings
//   falls    a spray burst going in and coming out, water sheeting off the shoulders inside
//   cooling  steam hissing off the pack, scaled by the real cooling rate (and the heat there is
//            to cool), so players learn water = cool
//
// It's a ring buffer of camera / ground / upright quads whose motion is closed-form in the
// vertex shader (like particles.ts), so spawning writes 20 floats and nothing is allocated per
// frame. The first RES slots are persistent (the per-cowboy jet dimple) and are rewritten in
// place. Colours are uniforms (one palette for all water): warm sunlit cream, cool mid, soft
// teal shadow, deep teal for the dimple / ring troughs.

import * as THREE from 'three';
import { quadGeometry, hazeUniforms, HAZE_VS, HASH_GLSL, ATLAS_GLSL, premulMaterial, fxAtlas, linRGB } from './gpu';
import { PAL } from '../palette';

export const enum WK { Drop = 0, Puff = 1, Steam = 2, Crown = 3, Ring = 4, Foam = 5, Dimple = 6, Streak = 7, Ribbon = 8 }

const STRIDE = 20;
/** persistent slots (one jet dimple per tracked cowboy) */
/** tracked cowboys */
const MAXC = 8;
/** rooster-tail ribbons per cowboy (persistent, like the dimple) */
const RIB = 3;
const RES = MAXC * (1 + RIB);
const NO_WATER = -1e8;

const VS = /* glsl */ `
${HAZE_VS}
attribute vec4 aA; // p0.xyz, t0
attribute vec4 aB; // v.xyz, life
attribute vec4 aC; // gravity, drag, floor, kind
attribute vec4 aD; // size0, size1, seed, alpha0
attribute vec4 aE; // alpha1, ex, ez, h (crown height)
uniform float uTime;
varying vec2 vUv;
varying float vKind;
varying float vLife;
varying float vSeed;
varying float vA;
varying float vHaze;
varying vec3 vHazeCol;
varying vec2 vSunL;
varying vec3 vSunV;
varying vec4 vCr;
varying float vFlat;
varying float vBack;
void hide() { gl_Position = vec4( 2.0, 2.0, 2.0, 1.0 ); }
void main() {
  float age = uTime - aA.w;
  float life = aB.w;
  if ( age < 0.0 || age > life ) { hide(); return; }
  float lt = age / life;
  int kind = int( aC.w + 0.5 );
  float ease = 1.0 - ( 1.0 - lt ) * ( 1.0 - lt );
  vec3 g = vec3( 0.0, -aC.x, 0.0 );
  float k = max( aC.y, 0.001 );
  float e = exp( -k * age );
  vec3 pos = aA.xyz + ( aB.xyz - g / k ) * ( 1.0 - e ) / k + ( g / k ) * age;
  vec3 vel = ( aB.xyz - g / k ) * e + g / k;
  // spray that falls back through the surface is gone (its own ring was scheduled on the CPU)
  if ( ( kind == 0 || kind == 7 ) && pos.y < aC.z && vel.y < 0.0 ) { hide(); return; }
  float size = mix( aD.x, aD.y, ease );
  vA = mix( aD.w, aE.x, lt );
  vKind = aC.w; vLife = lt; vSeed = aD.z;
  vCr = vec4( 0.0 );
  vSunV = normalize( ( viewMatrix * vec4( uSunDir, 0.0 ) ).xyz );
  // looking into the sun: thin water glows (translucency), strongest round the sun
  vBack = smoothstep( 0.1, 0.9, -vSunV.z );
  vec3 right = vec3( viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0] );
  vec3 up = vec3( viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1] );
  vec2 q = position.xy; // -0.5..0.5
  vec3 wp;
  vUv = q * 2.0;
  vSunL = normalize( vSunV.xy + vec2( 1e-4 ) );
  float near = 1.0;
  float camD = length( pos - cameraPosition );
  if ( kind >= 4 && kind <= 6 ) {
    // flat on the water: ellipse stretched along (ex, ez)
    vec2 ed = aE.yz;
    float el = length( ed );
    // (no division inside a ternary: SwiftShader evaluates both sides, and 0/0 poisons the select)
    vec2 gax = vec2( 1.0, 0.0 );
    if ( el > 1e-4 ) gax = ed / el;
    float st = max( el, 1.0 );
    float ext = size;
    if ( kind == 4 ) { vCr.x = size / max( aD.y, 1e-3 ); ext = aD.y; }
    vec2 lp = vec2( q.x * st, q.y ) * ext * 2.0;
    wp = pos + vec3( gax.x * lp.x - gax.y * lp.y, 0.06 + aD.z * 0.012, gax.y * lp.x + gax.x * lp.y );
    vec2 s2 = normalize( uSunDir.xz + vec2( 1e-4 ) );
    vSunL = vec2( dot( s2, gax ), dot( s2, vec2( -gax.y, gax.x ) ) );
    // world radius of the quad, for line widths that hold up at range
    vCr.y = ext;
    if ( kind == 5 ) vCr.z = 1.0 - smoothstep( 4.0, 10.0, camD );
    near = aE.w > 0.5 ? smoothstep( 2.4, 4.2, camD ) : smoothstep( 2.2, 5.0, camD );
  } else if ( kind == 3 ) {
    // splash crown: one card of a lathed shell (the CPU lays 6-12 of them round a circle, far
    // side first). The wall flares out as it rises; the points live in the fragment shader.
    float H = aE.w * sin( min( lt / 0.3, 1.0 ) * 1.5708 ) * ( 1.0 - 0.85 * smoothstep( 0.45, 1.0, lt ) );
    float a0 = atan( aE.z, aE.y );
    float v = q.y + 0.5;
    float th = a0 + q.x * 2.0 * aC.z * 1.2;
    vec2 rd = vec2( cos( th ), sin( th ) );
    float hh = max( H, 0.02 ) * 1.4; // headroom for the points
    wp = pos + vec3( rd.x, 0.0, rd.y ) * size * ( 1.0 + aE.x * pow( v, 1.6 ) ) + vec3( 0.0, v * hh - 0.03, 0.0 );
    vA = aD.w * ( 1.0 - 0.1 * lt ); // (aE.x carries the flare for a crown)
    // outward normal, tipped down by the flare; seen from inside it's the cup's lit floor side
    vec3 N = normalize( vec3( rd.x, -0.45, rd.y ) );
    float fc = dot( N, cameraPosition - wp ) > 0.0 ? 1.0 : -1.0;
    vCr = vec4( th, v * 1.4, dot( N * fc, uSunDir ), fc );
    near = smoothstep( 1.0, 3.0, camD );
  } else if ( kind == 8 ) {
    // rooster-tail ribbon: a tapered strip arcing up and back from under the pack, drawn in the
    // vertical plane through (ex, ez): back along the path and splayed out to one side
    float t = q.x + 0.5, v = q.y + 0.5;
    vec3 bk = vec3( aE.y, 0.0, aE.z );
    wp = pos + bk * t + vec3( 0.0, v * aD.y, 0.0 );
    vUv = vec2( t, v );
    vCr = vec4( aC.z / max( aD.y, 0.3 ), 0.0, 0.0, 0.0 );
    // spray between the chase camera and the rider fades out within ~3 m of the lens
    near = smoothstep( 2.6, 4.2, length( wp - cameraPosition ) );
  } else {
    if ( camD < 1.2 ) { hide(); return; }
    near = aE.w > 0.5 ? ( kind == 1 ? smoothstep( 3.0, 4.6, camD ) : smoothstep( 2.6, 4.8, camD ) ) : smoothstep( 2.4, 5.5, camD );
    // hold their size on screen at posse range (a teammate's spray at 30 m still reads as spray)
    size *= clamp( camD / 12.0, 1.0, 2.0 );
    size = min( size, camD * 0.22 );
    if ( kind == 0 || kind == 7 ) {
      // drops / streaks stretch along their screen velocity
      vec2 d2 = vec2( dot( vel, right ), dot( vel, up ) );
      float sp = length( d2 );
      d2 = sp > 1e-4 ? d2 / max( sp, 1e-4 ) : vec2( 0.0, 1.0 );
      vec3 sax = right * d2.x + up * d2.y;
      vec3 sd = right * d2.y - up * d2.x;
      // drops are teardrops stretched along their flight; streaks are long spray lines
      float len = kind == 0 ? size * ( 1.3 + min( sp * 0.06, 1.6 ) ) : size * ( 2.5 + min( sp * 0.35, 9.0 ) );
      float wid = kind == 0 ? size * 1.15 : size * 0.4;
      wp = pos + sax * q.y * len + sd * q.x * wid;
      vSunL = vec2( dot( vSunL, vec2( d2.y, -d2.x ) ), dot( vSunL, d2 ) );
    } else {
      float a = aD.z * 6.2832 + age * ( aD.z - 0.5 ) * 1.6;
      float c = cos( a ), s = sin( a );
      vec2 r2 = vec2( c * q.x - s * q.y, s * q.x + c * q.y );
      wp = pos + ( right * r2.x + up * r2.y ) * size;
      // puff atlas samples undo the spin in the fragment shader; keep the rotation in vCr
      vCr = vec4( c, s, 1.0 - smoothstep( 4.0, 9.0, camD ), 0.0 );
    }
  }
  gl_Position = projectionMatrix * viewMatrix * vec4( wp, 1.0 );
  vA *= near;
  vHaze = hazeAt( pos, vHazeCol );
  vFlat = 1.0 - smoothstep( 0.05, 0.12, size * projectionMatrix[1][1] / max( gl_Position.w, 1e-3 ) );
}
`;

const FS = /* glsl */ `
uniform float uNight;
uniform float uTime;
uniform vec3 uLit;   // warm sunlit cream
uniform vec3 uMid;   // cool white
uniform vec3 uShd;   // soft teal shadow
uniform vec3 uDeep;  // deep teal (troughs, dimple, crown interior)
uniform vec3 uRim;   // gold rim light
uniform vec3 uGrn;   // teal-green shadow underside of spray (#3E5A4E)
${HASH_GLSL}
${ATLAS_GLSL}
varying vec2 vUv;
varying float vKind;
varying float vLife;
varying float vSeed;
varying float vA;
varying float vHaze;
varying vec3 vHazeCol;
varying vec2 vSunL;
varying vec3 vSunV;
varying vec4 vCr;
varying float vFlat;
varying float vBack;
void main() {
  vec2 p = vUv;
  int kind = int( vKind + 0.5 );
  float a = 0.0;
  vec3 col = uLit;
  float hz = 0.5;
  if ( kind == 0 ) {
    // a teardrop along its flight (+y forward): round head, tapering tail; warm cream body,
    // a haze-gold glint, the shadow side teal-green
    float hy = 0.42, hr = 0.55;
    vec2 hp = vec2( p.x, p.y - hy ) / hr;
    float halfw = hr * pow( clamp( ( p.y + 1.0 ) / ( 1.0 + hy ), 0.0, 1.0 ), 1.2 );
    float r = p.y > hy ? length( hp ) : abs( p.x ) / max( halfw, 1e-3 );
    if ( p.y < -1.0 ) r = 2.0;
    a = 1.0 - smoothstep( 0.82, 1.0, r );
    vec2 nrm = p.y > hy ? hp : vec2( p.x / max( halfw, 1e-3 ), 0.25 );
    float l = dot( normalize( nrm + vec2( 1e-4 ) ), vSunL );
    vec3 shdG = mix( uGrn, uShd, 0.45 );
    col = l > -0.15 ? uLit : mix( shdG, mix( uMid, uLit, 0.5 ), vBack * 0.4 );
    // haze-gold glint on the head
    col = mix( col, uRim, step( length( hp - vSunL * 0.45 ), 0.32 ) * 0.8 );
    col = mix( col, uRim, vBack * 0.45 * smoothstep( 0.2, 0.9, r ) );
    a *= vA;
    hz = 0.45;
  } else if ( kind == 7 ) {
    // a thin spray streak / steam hiss jet
    // a spray streak: bright head, tail tapering back along the flight
    float wx = 0.95 * ( 0.3 + 0.7 * ( p.y * 0.5 + 0.5 ) );
    a = ( 1.0 - smoothstep( wx * 0.45, wx, abs( p.x ) ) ) * ( 1.0 - smoothstep( 0.75, 1.0, p.y ) ) * smoothstep( -1.0, -0.35, p.y ) * vA;
    col = mix( mix( uGrn, uLit, 0.55 ), uLit, smoothstep( -0.4, 0.3, p.y ) );
    hz = 0.45;
  } else if ( kind == 1 || kind == 2 ) {
    // lit atlas puff: spray mist (hard printed erosion) or steam (feathered)
    vec2 sp = vec2( vCr.x * p.x + vCr.y * p.y, -vCr.y * p.x + vCr.x * p.y );
    vec3 t = tile( floor( vSeed * 4.0 ), sp * 0.98 );
    float n = vnoise( sp * 3.3 + vSeed * 37.0 );
    bool steam = kind == 2;
    // right by the lens the printed erosion reads as camo: there it's a solid blob that fades
    float ns = steam ? 0.0 : vCr.z;
    float d = t.r * mix( steam ? 0.72 + 0.56 * n : 0.86 + 0.28 * n, 1.0, ns );
    float th = mix( mix( 0.1, 0.96, pow( vLife, 1.3 ) ), 0.12 + 0.2 * vLife, ns );
    a = steam ? smoothstep( th * 0.6, th * 0.6 + 0.5, d ) * ( 1.0 - smoothstep( 0.5, 1.0, length( sp ) ) ) * 0.85
              : smoothstep( th, th + 0.07, d );
    a *= mix( 1.0, 1.0 - smoothstep( 0.5, 1.0, vLife ), ns );
    a = mix( a, ( 1.0 - smoothstep( 0.3, 0.95, length( sp ) ) ) * ( 1.0 - smoothstep( 0.5, 1.0, vLife ) ) * 0.8, vFlat );
    vec2 nl = ( t.gb - 0.5 ) * 2.0;
    nl = vec2( vCr.x * nl.x - vCr.y * nl.y, vCr.y * nl.x + vCr.x * nl.y );
    vec3 N = normalize( vec3( nl * 2.2, 0.45 ) );
    float l = mix( dot( N, vSunV ), 0.3, vFlat );
    float bw = steam ? 0.3 : 0.05;
    // spray is cream wherever the sun reaches; teal only in the real shadow
    vec3 shd = steam ? mix( uShd, uMid, 0.75 ) : mix( uShd, uLit, 0.2 );
    col = mix( shd, mix( uMid, uLit, 0.55 ), smoothstep( -0.45 - bw, -0.45 + bw, l ) );
    col = mix( col, uLit, smoothstep( 0.0 - bw, 0.0 + bw, l ) );
    col = mix( col, uLit, ns * 0.35 );
    float rim = ( 1.0 - smoothstep( th + 0.05, th + 0.16, d ) ) * smoothstep( 0.0, 0.3, dot( normalize( vec3( nl, 0.2 ) ), vSunV ) );
    col = mix( col, uRim, rim * ( steam ? 0.3 : 0.55 ) );
    if ( steam ) col = mix( col, uLit, 0.3 );
    // backlit spray is translucent: the shadow side fills with warm light instead of going blue
    col = mix( col, mix( uMid, uLit, 0.5 ), vBack * 0.55 * ( 1.0 - smoothstep( -0.2, 0.4, l ) ) );
    col = mix( col, uRim, vBack * ( 0.2 + 0.45 * ( 1.0 - smoothstep( th, th + 0.35, d ) ) ) );
    a *= vA;
    hz = 0.55;
  } else if ( kind == 3 ) {
    // splash crown card: a flaring shell wall with sharp points on the rim
    float u = vCr.x;   // across the card
    float hv = vCr.y;  // 0 at the water, 1 at the rim (points reach above)
    float l = vCr.z;
    bool outside = vCr.w > 0.0;
    // u is the world angle round the crown (cards share it and the seed, so overlaps agree):
    // 22 points round the circle
    float sx = u * 3.50141 + vSeed * 7.0;
    float cell = mod( floor( sx ), 22.0 );
    float tri = 1.0 - abs( fract( sx ) * 2.0 - 1.0 );
    float top = 0.9 + pow( tri, 2.4 ) * ( 0.14 + 0.36 * h11( cell + vSeed * 31.0 ) );
    float aa = fwidth( hv ) * 1.2 + 0.012;
    // it tears up from the rim as it collapses
    float tear = vnoise( vec2( u * 3.0 + vSeed * 7.0, hv * 3.0 ) );
    float tth = smoothstep( 0.5, 1.0, vLife ) * 1.1 + hv * 0.3 - 0.2;
    a = ( 1.0 - smoothstep( top - aa, top + aa, hv ) ) * smoothstep( tth - 0.04, tth + 0.04, tear );
    // graded: a bright solid rim over a see-through base
    float hn = hv / max( top, 0.5 );
    a *= mix( 0.3, 1.0, smoothstep( 0.05, 0.7, hn ) );
    // hard two-tone print: warm cream in the sun, soft teal in shadow, the inside a step down
    vec3 warm = mix( uMid, uLit, 0.65 );
    vec3 shdc = mix( uShd, warm, 0.3 );
    col = l > 0.02 ? mix( warm, uLit, smoothstep( 0.02, 0.45, l ) ) : shdc;
    if ( !outside ) col = mix( col, shdc, 0.3 );
    // the upper sheet catches more light (a printed band)
    float band = smoothstep( -0.03, 0.03, hn - 0.6 - 0.1 * ( vnoise( vec2( u * 3.0, vSeed * 3.0 ) ) - 0.5 ) );
    col = mix( col, uLit, band * step( -0.15, l ) * 0.55 );
    // translucent rim lip: cream, gold when backlit
    float lip = 1.0 - smoothstep( 0.0, 0.12, top - hv );
    col = mix( col, mix( uLit, uRim, 0.25 + 0.75 * vBack ), lip * 0.85 );
    col = mix( col, uRim, vBack * 0.3 * smoothstep( 0.35, 1.0, hv ) );
    // the thin base takes the water's teal
    col = mix( mix( shdc, warm, 0.45 ), col, smoothstep( 0.0, 0.55, hn ) );
    a *= vA;
    hz = 0.5;
  } else if ( kind == 8 ) {
    // rooster-tail ribbon: rises steeply, arcs over, starts to fall; thin at the root, fat and
    // breaking up at the end; cream core, teal-green underside, gold top edge into the sun
    float t = p.x, v = p.y;
    float c = sin( t * 1.95 ) * 0.7; // rises steeply and keeps climbing as it goes back
    float w = vCr.x * ( 0.3 + 1.4 * t ) * ( 1.0 - 0.45 * smoothstep( 0.75, 1.0, t ) );
    float n = vnoise( vec2( t * 7.0 - uTime * 3.5 + vSeed * 9.0, v * 5.0 ) );
    float edge = w * ( 0.75 + 0.5 * n );
    float dv = v - c;
    float aa = fwidth( dv ) * 1.2 + 0.003;
    a = 1.0 - smoothstep( edge - aa, edge + aa, abs( dv ) );
    a *= 1.0 - smoothstep( 0.55, 1.0, t + ( n - 0.5 ) * 0.35 );
    a *= smoothstep( 0.0, 0.05, t );
    float under = smoothstep( -edge * 0.3, -edge * 0.85, dv );
    col = mix( uLit, mix( uGrn, uShd, 0.3 ), under * 0.9 );
    col = mix( col, uRim, smoothstep( edge * 0.4, edge, dv ) * ( 0.3 + 0.5 * vBack ) );
    a *= vA;
    hz = 0.5;
  } else if ( kind == 4 ) {
    // ripple ring: a lit crest with a soft teal trough inside it, broken like a printed line
    float d = length( p );
    float ang = atan( p.y, p.x );
    float rr = vCr.x;
    float wob = ( vnoise( vec2( ang * 1.6 + vSeed * 20.0, vSeed * 7.0 ) ) - 0.5 ) * 0.07 + ( vnoise( vec2( ang * 5.0, vSeed * 3.0 ) ) - 0.5 ) * 0.025;
    float w = clamp( 0.035 / max( vCr.y, 0.2 ), 0.01, 0.11 ) * ( 1.2 - 0.55 * vLife );
    float x = d - rr - wob;
    float crest = 1.0 - smoothstep( w * 0.4, w, abs( x ) );
    float trough = smoothstep( -w * 5.0, -w * 1.6, x ) * ( 1.0 - smoothstep( -w * 1.6, -w * 0.6, x ) );
    // breaks open here and there as it spreads (not a dashed UI ring)
    float brk = smoothstep( 0.12 + 0.45 * vLife, 0.3 + 0.45 * vLife, vnoise( vec2( ang * 2.2 + vSeed * 31.0, rr * 1.5 + vSeed ) ) );
    crest *= brk;
    trough *= 0.6 + 0.4 * brk;
    float sunF = dot( normalize( p + vec2( 1e-4 ) ), vSunL );
    vec3 cc = mix( uMid, uLit, smoothstep( -0.3, 0.6, sunF ) );
    float ac = crest * 0.85, at = trough * 0.22;
    a = max( ac, at );
    col = ac >= at ? cc : uDeep;
    a *= vA * ( 1.0 - smoothstep( 0.92, 1.0, d ) );
    hz = 0.45;
  } else if ( kind == 5 ) {
    // surface foam: lacy, eroding patches of cream with teal in the holes
    float rot = vSeed * 6.2832;
    vec2 sp = vec2( cos( rot ) * p.x - sin( rot ) * p.y, sin( rot ) * p.x + cos( rot ) * p.y );
    vec3 t = tile( floor( vSeed * 4.0 ), sp * 0.95 );
    float nf = vCr.z; // near the lens: solid white water, no lace
    float d = t.r * mix( 0.65 + 0.7 * vnoise( sp * 3.0 + vSeed * 11.0 ), 1.0, nf );
    float th = mix( mix( 0.12, 0.92, pow( vLife, 0.9 ) ), 0.12 + 0.25 * vLife, nf );
    a = smoothstep( th, th + 0.05, d ) * mix( 1.0, 1.0 - smoothstep( 0.5, 1.0, vLife ), nf );
    float lace = vnoise( sp * 8.5 + vSeed * 17.0 + uTime * 0.15 );
    a *= 1.0 - step( 0.66 - 0.2 * vLife, lace ) * 0.9 * ( 1.0 - nf );
    float edge = smoothstep( th + 0.03, th + 0.22, d );
    col = mix( uShd, uLit, 0.35 + 0.65 * edge );
    // a faint trough ring around the patch (water pushed away)
    float ring = ( 1.0 - smoothstep( th - 0.12, th, d ) ) * smoothstep( th - 0.3, th - 0.12, d );
    float at = ring * 0.18;
    if ( at > a ) { a = at; col = uDeep; }
    a *= vA * ( 1.0 - smoothstep( 0.85, 1.0, length( p ) ) );
    hz = 0.45;
  } else if ( kind == 6 ) {
    // the jet dimple: a dark churning pit, a ring of torn foam, spokes of spray thrown out
    float d = length( p );
    float ang = atan( p.y, p.x );
    float sw = uTime * ( 2.5 + vSeed );
    float n1 = vnoise( vec2( ang * 3.0 + sw, d * 4.0 - uTime * 7.0 ) );
    float n2 = vnoise( vec2( ang * 7.0 - sw * 1.3, d * 9.0 - uTime * 11.0 + vSeed * 9.0 ) );
    float pit = 1.0 - smoothstep( 0.22, 0.42 + 0.06 * n1, d );
    float rim = smoothstep( 0.24, 0.4, d ) * ( 1.0 - smoothstep( 0.55 + 0.2 * n1, 0.78, d ) );
    float foam = rim * step( 0.38, n2 );
    float spokes = step( 0.7, vnoise( vec2( ang * 8.0, uTime * 3.0 + vSeed * 5.0 ) ) ) * smoothstep( 0.45, 0.6, d ) * ( 1.0 - smoothstep( 0.6 + 0.35 * n2, 0.98, d ) );
    float f = max( foam, spokes * 0.85 );
    float sunF = dot( normalize( p + vec2( 1e-4 ) ), vSunL );
    vec3 fc = mix( uMid, uLit, smoothstep( -0.4, 0.5, sunF ) );
    // pit swirl lines
    float swirl = step( 0.62, vnoise( vec2( ang * 4.0 + d * 6.0 - sw * 2.0, vSeed ) ) ) * pit;
    float ap = pit * 0.5 * ( 1.0 - swirl * 0.6 );
    a = max( f, ap );
    col = f >= ap ? fc : mix( uDeep, uShd, swirl );
    a *= vA;
    hz = 0.45;
  }
  if ( a < 0.01 ) discard;
  col *= mix( vec3( 1.0 ), vec3( 0.3, 0.36, 0.52 ), uNight );
  col = mix( col, vHazeCol, vHaze * hz );
  gl_FragColor = vec4( col * a, a );
}
`;

/** Ring buffer with RES persistent slots at the front (rewritten in place each frame). */
class WRing {
  readonly arr: Float32Array;
  readonly buf: THREE.InstancedInterleavedBuffer;
  private head = RES;
  private start = RES;
  private n = 0;
  private touchLo = RES;
  private touchHi = -1;
  private pendingFull = false;
  private ranges: { start: number; count: number }[] = [];
  constructor(readonly cap: number) {
    this.arr = new Float32Array(cap * STRIDE);
    this.buf = new THREE.InstancedInterleavedBuffer(this.arr, STRIDE, 1);
    this.buf.setUsage(THREE.DynamicDrawUsage);
    for (let i = 0; i < 24; i++) this.ranges.push({ start: 0, count: 0 });
    this.buf.onUpload(() => { this.pendingFull = false; });
  }
  alloc(): number {
    const i = this.head;
    if (this.n === 0) this.start = i;
    this.n++;
    this.head = i + 1 === this.cap ? RES : i + 1;
    return i * STRIDE;
  }
  touch(slot: number) {
    if (slot < this.touchLo) this.touchLo = slot;
    if (slot > this.touchHi) this.touchHi = slot;
  }
  attr(size: number, offset: number) { return new THREE.InterleavedBufferAttribute(this.buf, size, offset); }
  flush() {
    const touched = this.touchHi >= this.touchLo;
    if (this.n === 0 && !touched) return;
    const b = this.buf;
    let k = b.updateRanges.length;
    const span = this.cap - RES;
    if (this.pendingFull || this.n >= span || k + 3 > this.ranges.length) {
      b.updateRanges.length = 0;
      this.pendingFull = true;
    } else {
      if (this.n > 0) {
        const end = this.start + this.n;
        if (end <= this.cap) {
          const r = this.ranges[k++]; r.start = this.start * STRIDE; r.count = this.n * STRIDE; b.updateRanges.push(r);
        } else {
          const r = this.ranges[k++]; r.start = this.start * STRIDE; r.count = (this.cap - this.start) * STRIDE; b.updateRanges.push(r);
          const r2 = this.ranges[k++]; r2.start = RES * STRIDE; r2.count = (end - this.cap) * STRIDE; b.updateRanges.push(r2);
        }
      }
      if (touched) {
        const r = this.ranges[k++]; r.start = this.touchLo * STRIDE; r.count = (this.touchHi - this.touchLo + 1) * STRIDE; b.updateRanges.push(r);
      }
    }
    b.needsUpdate = true;
    this.n = 0; this.touchLo = RES; this.touchHi = -1;
  }
}

const rnd = (a: number, b: number) => a + Math.random() * (b - a);
const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
const sstep = (a: number, b: number, x: number) => { const t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); };

// the sim's cooling rates (sim/player.ts WADE_COOL / SKIM_COOL / FALL_COOL, WATER.skimH): mirrored
// here only to scale the steam; the sim stays the authority on heat
const WADE_COOL = 40, SKIM_COOL = 62, FALL_COOL = 140, SKIM_H = 4;
const HOVER_H = 7;

export class WaterFx {
  readonly mesh: THREE.Mesh;
  private ring: WRing;
  private readonly uniforms = { uTime: { value: 0 } };
  private time = 0;
  private mul: number;
  private camX = 0; private camY = 0; private camZ = 0;
  /** 1 while emitting for the local cowboy: his own spray fades sooner near the lens (it's all between him and the camera) */
  private own = 0;
  /** >= 0: the seed every emit uses (a crown's cards share one) */
  private seed = -1;
  private tRib = new Uint8Array(MAXC);
  /** impact strength 0..1 of the last splash cowboy() reported (for the sound) */
  lastSplash = 0;
  /** heat fraction when the last cooling started (for the hiss) */
  lastCool = 0;
  /** where the last event happened */
  readonly evPos = { x: 0, y: 0, z: 0 };
  // per-cowboy trackers (struct of arrays)
  private tId = new Int32Array(MAXC).fill(-1);
  private tSeen = new Uint8Array(MAXC);
  private tFresh = new Uint8Array(MAXC);
  private tAbove = new Float32Array(MAXC);
  private tVy = new Float32Array(MAXC);
  private tX = new Float32Array(MAXC); private tZ = new Float32Array(MAXC);
  private tFall = new Float32Array(MAXC);
  private tCool = new Float32Array(MAXC);
  private tStride = new Float32Array(MAXC); private tSide = new Int8Array(MAXC);
  private tDist = new Float32Array(MAXC); private tDist2 = new Float32Array(MAXC); private tDist3 = new Float32Array(MAXC);
  private tIdle = new Float32Array(MAXC);
  private tWet = new Float32Array(MAXC).fill(99);
  /** 1 after leaving a waterfall: the drip trail is heavier until he's dry */
  private tDrip = new Uint8Array(MAXC);
  private tDimple = new Uint8Array(MAXC);
  // emitter accumulators: 0 drops 1 puffs 2 wings 3 bow 4 corona 5 mist 6 rings 7 drips 8 steam 9 hiss 10 fall
  private acc = new Float32Array(MAXC * 12);

  constructor(quality: number) {
    const q = Math.max(0, Math.min(3, quality | 0));
    this.mul = [0.45, 0.75, 1, 1.2][q];
    const cap = [1024, 2048, 3072, 4096][q];
    this.ring = new WRing(cap);
    const g = quadGeometry(cap);
    const r = this.ring;
    g.setAttribute('aA', r.attr(4, 0));
    g.setAttribute('aB', r.attr(4, 4));
    g.setAttribute('aC', r.attr(4, 8));
    g.setAttribute('aD', r.attr(4, 12));
    g.setAttribute('aE', r.attr(4, 16));
    for (let i = 0; i < cap; i++) { r.arr[i * STRIDE + 3] = -1e6; r.arr[i * STRIDE + 7] = 0; }
    const c = (hex: number, k = 1) => { const v = linRGB(hex); return new THREE.Vector3(v[0] * k, v[1] * k, v[2] * k); };
    const m = premulMaterial(new THREE.ShaderMaterial({
      vertexShader: VS, fragmentShader: FS,
      uniforms: {
        ...hazeUniforms(), ...this.uniforms, tAtlas: { value: fxAtlas() },
        uLit: { value: c(0xfff3dc, 1.12) },
        uMid: { value: c(0xe4f0ec) },
        uShd: { value: c(0x9ec6c0) },
        uDeep: { value: c(0x2f6b6e) },
        uRim: { value: c(PAL.sunCore, 1.2) },
        uGrn: { value: c(0x3e5a4e) },
      },
    }));
    // ground quads face down in their local winding and are seen from above and below: no culling
    m.side = THREE.DoubleSide;
    this.mesh = new THREE.Mesh(g, m);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 11;
    this.mesh.name = 'waterFx';
  }

  update(time: number, camX: number, camY: number, camZ: number) {
    this.time = time;
    this.camX = camX; this.camY = camY; this.camZ = camZ;
    this.uniforms.uTime.value = time;
    this.ring.flush();
  }

  /** live instance count estimate is not tracked; capacity is the ceiling */
  get capacity() { return this.ring.cap; }

  // ---------------------------------------------------------------- primitive
  emit(kind: number, x: number, y: number, z: number, vx: number, vy: number, vz: number,
    life: number, delay: number, grav: number, drag: number, floor: number,
    s0: number, s1: number, a0: number, a1: number, ex = 0, ez = 0, h = 0) {
    if (!(x + y + z + vx + vy + vz === x + y + z + vx + vy + vz)) return;
    const a = this.ring.arr, o = this.ring.alloc();
    a[o] = x; a[o + 1] = y; a[o + 2] = z; a[o + 3] = this.time + delay;
    a[o + 4] = vx; a[o + 5] = vy; a[o + 6] = vz; a[o + 7] = life;
    a[o + 8] = grav; a[o + 9] = drag; a[o + 10] = floor; a[o + 11] = kind;
    a[o + 12] = s0; a[o + 13] = s1; a[o + 14] = this.seed >= 0 ? this.seed : Math.random(); a[o + 15] = a0;
    a[o + 16] = a1; a[o + 17] = ex; a[o + 18] = ez; a[o + 19] = kind === WK.Crown ? h : this.own;
  }
  private setDimple(slot: number, on: boolean, x: number, y: number, z: number, r: number, str: number, ex: number, ez: number) {
    const a = this.ring.arr, o = slot * STRIDE;
    if (!on) {
      if (!this.tDimple[slot]) return;
      this.tDimple[slot] = 0;
      a[o + 7] = 0; a[o + 3] = -1e6;
      this.ring.touch(slot);
      return;
    }
    if (!this.tDimple[slot]) { a[o + 14] = Math.random(); this.tDimple[slot] = 1; }
    a[o] = x; a[o + 1] = y; a[o + 2] = z; a[o + 3] = this.time - 1;
    a[o + 4] = 0; a[o + 5] = 0; a[o + 6] = 0; a[o + 7] = 1e7;
    a[o + 8] = 0; a[o + 9] = 1; a[o + 10] = 0; a[o + 11] = WK.Dimple;
    a[o + 12] = r; a[o + 13] = r; a[o + 15] = str;
    a[o + 16] = str; a[o + 17] = ex; a[o + 18] = ez; a[o + 19] = 0;
    this.ring.touch(slot);
  }
  /** One rooster-tail ribbon of tracked cowboy s (persistent slot, rewritten each frame). */
  private setRibbon(s: number, j: number, on: boolean, x = 0, wy = 0, z = 0, bx = 0, bz = 0, hq = 0, hw = 0, al = 0) {
    const slot = MAXC + s * RIB + j, a = this.ring.arr, o = slot * STRIDE, bit = 1 << j;
    if (!on) {
      if (!(this.tRib[s] & bit)) return;
      this.tRib[s] &= ~bit;
      a[o + 7] = 0; a[o + 3] = -1e6;
      this.ring.touch(slot);
      return;
    }
    if (!(this.tRib[s] & bit)) { a[o + 14] = Math.random(); this.tRib[s] |= bit; }
    a[o] = x; a[o + 1] = wy; a[o + 2] = z; a[o + 3] = this.time - 1;
    a[o + 4] = 0; a[o + 5] = 0; a[o + 6] = 0; a[o + 7] = 1e7;
    a[o + 8] = 0; a[o + 9] = 1; a[o + 10] = hw; a[o + 11] = WK.Ribbon;
    a[o + 12] = hq; a[o + 13] = hq; a[o + 15] = al;
    a[o + 16] = al; a[o + 17] = bx; a[o + 18] = bz; a[o + 19] = this.own;
    this.ring.touch(slot);
  }
  private ribbonsOff(s: number) { for (let j = 0; j < RIB; j++) this.setRibbon(s, j, false); }
  /** LOD: far cowboys spray less (they're a few pixels anyway) */
  private lod(x: number, y: number, z: number) {
    const d = Math.hypot(x - this.camX, y - this.camY, z - this.camZ);
    return Math.max(0.25, Math.min(1, 1.5 - d / 70));
  }
  private take(i: number, rate: number, dt: number) {
    let v = this.acc[i] + rate * dt;
    if (v > 40) v = 40;
    const n = Math.floor(v);
    this.acc[i] = v - n;
    return n;
  }

  /**
   * A crown is n cards round a circle making one flaring shell (kind Crown: aC.z = the card's
   * half-angle, ex/ez its direction). The far side goes into the ring first so the near wall
   * draws over it (one draw call, no sort).
   */
  private crown(cx: number, wy: number, cz: number, vx: number, vz: number, life: number, delay: number,
    drag: number, r0: number, r1: number, h: number, n: number, flare = 0.95) {
    const hw = Math.PI / n;
    this.seed = Math.random();
    const back = Math.atan2(this.camZ - cz, this.camX - cx) + Math.PI + rnd(-0.3, 0.3) * hw;
    for (let j = 0; j < n; j++) {
      const step = Math.ceil(j / 2) * (j % 2 ? 1 : -1);
      const a = back + step * 2 * hw;
      this.emit(WK.Crown, cx, wy, cz, vx, 0, vz, life, delay, 0, drag, hw, r0, r1, 1, flare, Math.cos(a), Math.sin(a), h);
    }
    this.seed = -1;
  }

  // ---------------------------------------------------------------- one-shots
  /**
   * Something hit the water: speed is the impact speed (m/s), (hx, hz) the horizontal velocity.
   * A soft landing (~5 m/s) is a small cup of a crown and a couple of rings; a 20 m dive (~35 m/s)
   * is a 3 m crown with a column, a rain of droplets and a wide foam slick.
   */
  splash(x: number, wy: number, z: number, speed: number, hx: number, hz: number) {
    const k = clamp01((speed - 2.5) / 28);
    const m = this.mul;
    const hs = Math.hypot(hx, hz);
    const ux = hs > 0.1 ? hx / hs : 0, uz = hs > 0.1 ? hz / hs : 0;
    // crown (pushed a little along the motion)
    const cx = x + ux * Math.min(hs, 20) * 0.03, cz = z + uz * Math.min(hs, 20) * 0.03;
    this.crown(cx, wy, cz, hx * 0.04, hz * 0.04, 0.55 + 0.55 * k, 0, 2, 0.28 + 0.32 * k, 0.6 + 1.45 * k, 0.32 + 2.25 * Math.pow(k, 1.5), k > 0.3 ? 12 : 9);
    if (k > 0.45) {
      // the column that shoots up after a dive
      // the column jet that shoots up inside the crown after a dive
      this.crown(cx, wy, cz, 0, 0, 0.75 + 0.4 * k, 0.06, 2, 0.12, 0.2 + 0.12 * k, 1.2 + 3 * k, 6, 0.3);
      const nj = Math.round(16 * m);
      for (let i = 0; i < nj; i++) {
        const a = Math.random() * 6.2832, o = rnd(0, 1.2);
        this.emit(WK.Streak, cx + Math.cos(a) * 0.12, wy + 0.2, cz + Math.sin(a) * 0.12, Math.cos(a) * o, rnd(10, 16) * (0.6 + 0.4 * k), Math.sin(a) * o,
          rnd(0.8, 1.1), rnd(0.05, 0.2), 18, 0.5, wy, rnd(0.08, 0.12), 0.07, 1, 0.85);
      }
    }
    // droplets off the rim, each printing its own little ring where it lands
    const nd = Math.round((10 + 70 * k) * m);
    const rim = 0.55 + 1.0 * k;
    for (let i = 0; i < nd; i++) {
      const a = Math.random() * 6.2832, ca = Math.cos(a), sa = Math.sin(a);
      const out = rnd(1.2, 2.5 + 5 * k), up = rnd(2.5, 4.5 + 9 * k) * (0.7 + 0.3 * Math.random());
      const vx = ca * out + hx * 0.25, vz = sa * out + hz * 0.25;
      const rr = rim * rnd(0.5, 1);
      const px = cx + ca * rr, pz = cz + sa * rr;
      const size = rnd(0.08, 0.13) + 0.1 * k * Math.random();
      this.emit(WK.Drop, px, wy + rnd(0.1, 0.4 + 0.6 * k), pz, vx, up, vz, 2.5, rnd(0, 0.12), 22, 0.35, wy, size, size * 0.8, 1, 1);
      if (i % 3 === 0) {
        // time to fall back: y(t) = h + up t - g t^2/2 (the drag is light)
        const t = (up + Math.sqrt(up * up + 2 * 22 * 0.25)) / 22;
        const f = (1 - Math.exp(-0.35 * t)) / 0.35;
        this.emit(WK.Ring, px + vx * f, wy, pz + vz * f, 0, 0, 0, 0.7, t, 0, 1, 0, 0.05, 0.35 + size * 2, 0.9, 0);
      }
    }
    // mist rolling off the base
    const nm = Math.round((2 + 10 * k) * m);
    for (let i = 0; i < nm; i++) {
      const a = Math.random() * 6.2832, ca = Math.cos(a), sa = Math.sin(a);
      this.emit(WK.Puff, cx + ca * rim * 0.6, wy + rnd(0.1, 0.5 + k), cz + sa * rim * 0.6, ca * rnd(1, 2.5 + 2 * k), rnd(0.3, 1.5 + 2 * k), sa * rnd(1, 2.5 + 2 * k),
        rnd(0.8, 1.3 + 0.6 * k), rnd(0.02, 0.15), 1.5, 2.2, -1e5, 0.3 + 0.2 * k, 0.8 + 1.2 * k, 0.95, 0.8);
    }
    // foam slick + rings
    this.emit(WK.Foam, cx, wy, cz, 0, 0, 0, 2.6 + 2.5 * k, 0.05, 0, 1, 0, 0.4 + 0.4 * k, 1.1 + 2.6 * k, 1, 0.9);
    if (k > 0.25) for (let i = 0; i < 3; i++) {
      const a = Math.random() * 6.2832, rr = rnd(0.6, 1.4) * rim;
      this.emit(WK.Foam, cx + Math.cos(a) * rr, wy, cz + Math.sin(a) * rr, Math.cos(a) * 0.8, 0, Math.sin(a) * 0.8, rnd(2, 3.5), rnd(0.1, 0.3), 0, 1.2, 0, 0.3, 0.8 + k, 1, 0.8);
    }
    const nr = 2 + Math.round(2 * k);
    for (let i = 0; i < nr; i++) {
      this.emit(WK.Ring, cx, wy, cz, 0, 0, 0, 1.3 + 0.9 * k, i * (0.14 + 0.06 * k), 0, 1, 0, 0.3 + 0.3 * k, 1.6 + 5.5 * k - i * 0.6 * k, 1, 0);
    }
  }

  /** Spray burst going into / out of a waterfall sheet. */
  fallBurst(x: number, y: number, z: number, vx: number, vy: number, vz: number, k: number) {
    const m = this.mul;
    // chunky drops thrown out round the body and along the way through
    const n = Math.round((34 + 40 * k) * m);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * 6.2832, ca = Math.cos(a), sa = Math.sin(a), s = rnd(2.5, 7.5);
      const sz = rnd(0.08, 0.15) + 0.05 * k * Math.random();
      this.emit(WK.Drop, x + ca * 0.35, y + rnd(0.3, 1.9), z + sa * 0.35, ca * s + vx * 0.45, rnd(0.5, 5.5), sa * s + vz * 0.45, 1.8, rnd(0, 0.08), 20, 0.5, -1e5,
        sz, sz * 0.7, 1, 0.9);
      if (i % 2 === 0) this.emit(WK.Streak, x, y + rnd(0.4, 1.9), z, ca * s * 1.2 + vx * 0.6, rnd(-0.5, 2.5) + vy * 0.3, sa * s * 1.2 + vz * 0.6, rnd(0.35, 0.6), 0, 10, 0.8, -1e5, 0.08, 0.06, 1, 0.3);
    }
    // a big burst of lit spray: a cloud blown out of the sheet, strung along the way through
    const np = Math.round((8 + 6 * k) * m);
    for (let i = 0; i < np; i++) {
      const a = Math.random() * 6.2832, o = rnd(2, 5.5), t = rnd(0, 0.12);
      this.emit(WK.Puff, x + Math.cos(a) * 0.5 + vx * t, y + rnd(0.2, 2.1), z + Math.sin(a) * 0.5 + vz * t,
        Math.cos(a) * o + vx * 0.45, rnd(-0.5, 2.5), Math.sin(a) * o + vz * 0.45,
        rnd(0.9, 1.5), t, 0.8, 2.0, -1e5, 0.4, 1.3 + 0.9 * k, 1, 0.75);
    }
  }

  // ---------------------------------------------------------------- per cowboy, every frame
  private slotOf(id: number) {
    let free = -1;
    for (let i = 0; i < MAXC; i++) { if (this.tId[i] === id) return i; if (free < 0 && this.tId[i] < 0) free = i; }
    if (free < 0) return -1;
    this.tId[free] = id; this.tFresh[free] = 1;
    return free;
  }
  /** call before the cowboys of a frame */
  begin() { this.tSeen.fill(0); }
  /** call after: forgets cowboys that weren't reported and drops their dimples */
  end() {
    for (let i = 0; i < MAXC; i++) {
      if (this.tId[i] >= 0 && !this.tSeen[i]) {
        this.setDimple(i, false, 0, 0, 0, 0, 0, 0, 0);
        this.ribbonsOff(i);
        this.tId[i] = -1;
      }
    }
  }

  /**
   * One cowboy's water this frame. y is the feet; wy the water surface under them (NO_WATER / < -1e7
   * if dry); fall 0..1 inside a waterfall; thr the jet throttle 0..1; heat 0..1 of the pack's max.
   * Returns event bits: 1 a splash (strength in lastSplash), 2 cooling started (lastCool), 4 into a fall.
   */
  cowboy(id: number, x: number, y: number, z: number, vx: number, vy: number, vz: number, yaw: number,
    move: number, grounded: boolean, wy: number, fall: number, thr: number, heat: number, dt: number, local = false): number {
    const s = this.slotOf(id);
    this.own = local ? 1 : 0;
    if (s < 0) return 0;
    this.tSeen[s] = 1;
    // deep under a surface (beneath a cascade lip, an overhang): not this cowboy's water
    const wet = wy > -1e7 && y > wy - 2.2;
    const above = wet ? y - wy : 1e3;
    // teleports (respawn, warp, first sight) never splash
    if (this.tFresh[s] || Math.hypot(x - this.tX[s], z - this.tZ[s]) > 6) {
      this.tFresh[s] = 0;
      this.tAbove[s] = above; this.tVy[s] = vy; this.tFall[s] = fall; this.tCool[s] = 0;
      this.tX[s] = x; this.tZ[s] = z;
      this.setDimple(s, false, 0, 0, 0, 0, 0, 0, 0);
      this.ribbonsOff(s);
      return 0;
    }
    const moved = Math.hypot(x - this.tX[s], z - this.tZ[s]);
    this.tX[s] = x; this.tZ[s] = z;
    let ev = 0;
    const m = this.mul * this.lod(x, y, z);
    const A = s * 12;
    const hs = Math.hypot(vx, vz);
    const ux = hs > 0.05 ? vx / hs : -Math.sin(yaw), uz = hs > 0.05 ? vz / hs : -Math.cos(yaw);
    const px = -uz, pz = ux; // right of travel
    let dimpleOn = false, ribOn = false;

    if (wet) {
      // ---- hitting the water: feet cross the surface on the way down
      const prevA = this.tAbove[s];
      if (prevA > 0.04 && above <= 0.04) {
        const vyi = Math.min(this.tVy[s], vy);
        const imp = Math.sqrt(vyi * vyi + 0.3 * hs * hs);
        if (imp > 3 && vyi < -1) {
          this.splash(x, wy, z, imp, vx, vz);
          this.lastSplash = clamp01((imp - 2.5) / 28);
          this.evPos.x = x; this.evPos.y = wy; this.evPos.z = z;
          ev |= 1;
        }
      }
      if (above <= 0.35 && (grounded || above < 0.1)) {
        // ---- wading
        this.tWet[s] = 0;
        const depth = clamp01(-above / 1.6);
        if (hs < 0.7) {
          this.tIdle[s] += dt;
          if (this.tIdle[s] > 1.25) {
            this.tIdle[s] = rnd(0, 0.3);
            this.emit(WK.Ring, x, wy, z, 0, 0, 0, 1.7, 0, 0, 1, 0, 0.3, 1.5, 0.75, 0);
          }
        } else {
          this.tIdle[s] = 0;
          // rings stamped along the path: older rings are wider, so together they draw the V
          this.tDist[s] += moved;
          if (this.tDist[s] > 0.42) {
            this.tDist[s] = 0;
            this.emit(WK.Ring, x + ux * 0.15, wy, z + uz * 0.15, 0, 0, 0, 1.25, 0, 0, 1, 0, 0.32, 0.9 + hs * 0.22, 0.95, 0, ux * 1.15, uz * 1.15);
          }
          // the soft V: foam pushed out to both sides
          this.tDist2[s] += moved;
          if (this.tDist2[s] > 0.55) {
            this.tDist2[s] = 0;
            const ls = 0.5 + hs * 0.14;
            for (let sd = -1; sd <= 1; sd += 2) {
              this.emit(WK.Foam, x + px * sd * 0.3 - ux * 0.1, wy, z + pz * sd * 0.3 - uz * 0.1, px * sd * ls + ux * hs * 0.12, 0, pz * sd * ls + uz * hs * 0.12,
                rnd(1.2, 1.7), 0, 0, 1.4, 0, 0.14, 0.32 + depth * 0.25, 0.9, 0.6, ux * 1.6, uz * 1.6);
            }
          }
          // bow churn at the legs / waist
          const nb = this.take(A + 3, (6 + hs * 2.2) * (0.6 + depth) * m, dt);
          for (let i = 0; i < nb; i++) {
            const sd = rnd(-1, 1);
            this.emit(WK.Foam, x + ux * 0.32 + px * sd * 0.22, wy, z + uz * 0.32 + pz * sd * 0.22, ux * hs * 0.5 + px * sd * 0.6, 0, uz * hs * 0.5 + pz * sd * 0.6,
              rnd(0.45, 0.7), 0, 0, 2.5, 0, 0.12, 0.3, 1, 0.5);
            if (hs > 2.5 && Math.random() < 0.6) this.emit(WK.Drop, x + ux * 0.35 + px * sd * 0.2, wy + 0.05, z + uz * 0.35 + pz * sd * 0.2,
              ux * hs * rnd(0.4, 0.9) + px * sd * 1.2, rnd(1.2, 2 + hs * 0.25), uz * hs * rnd(0.4, 0.9) + pz * sd * 1.2, 1.2, 0, 22, 0.4, wy, rnd(0.045, 0.075), 0.04, 1, 1);
          }
          // a splash per stride: a foot (or knee) breaks the surface
          const stride = hs > 5 ? 1.25 : 0.85;
          this.tStride[s] += moved;
          if (this.tStride[s] > stride) {
            this.tStride[s] = 0;
            const sd = (this.tSide[s] = this.tSide[s] > 0 ? -1 : 1);
            const fx = x + px * sd * 0.17 + ux * 0.28, fz = z + pz * sd * 0.17 + uz * 0.28;
            const kk = Math.min(1, hs / 7) * (1 - depth * 0.5);
            const nd = Math.round((4 + 9 * kk) * Math.max(0.5, m));
            for (let i = 0; i < nd; i++) {
              const a = Math.random() * 6.2832, o = rnd(0.4, 1.4);
              this.emit(WK.Drop, fx, wy + 0.05, fz, Math.cos(a) * o + ux * hs * rnd(0.3, 0.7), rnd(1.6, 2.4 + 2.2 * kk), Math.sin(a) * o + uz * hs * rnd(0.3, 0.7),
                1.2, rnd(0, 0.05), 22, 0.4, wy, rnd(0.045, 0.08) + 0.03 * kk, 0.04, 1, 1);
            }
            if (kk > 0.35) this.crown(fx, wy, fz, ux * hs * 0.3, uz * hs * 0.3, 0.38, 0, 3, 0.1, 0.22 + 0.1 * kk, 0.18 + 0.35 * kk, 6, 0.8);
            this.emit(WK.Ring, fx, wy, fz, 0, 0, 0, 0.8, 0.03, 0, 1, 0, 0.08, 0.5 + 0.3 * kk, 0.9, 0);
            this.emit(WK.Foam, fx, wy, fz, ux * hs * 0.2, 0, uz * hs * 0.2, 0.9, 0, 0, 2, 0, 0.12, 0.35 + 0.2 * kk, 1, 0.6);
          }
        }
      } else if (!grounded && above > -0.05) {
        this.tIdle[s] = 0;
        // ---- airborne over water
        const flight = move === 3;
        const sk = above < SKIM_H ? 1 - above / SKIM_H : 0;
        const sp = flight ? sstep(3, 14, hs) : 0;
        if (sk > 0 && sp > 0) {
          // rooster tail: a plume of lit spray sheets + chunky drops thrown up from under the pack,
          // left standing in the air behind as the cowboy blasts on (world-still, so it trails)
          const k = Math.pow(sk, 1.2) * sp;
          // the tail itself: tapered ribbons arcing 1.5-2.5 m up behind the rider, splayed into a V
          ribOn = true;
          const H = 1.5 + 1.0 * k, L = 1.5 + 0.9 * sp;
          for (let j = 0; j < RIB; j++) {
            const sd = j === 0 ? -1 : j === 1 ? 1 : 0.4, hj = j === 2 ? 0.8 : 1, lw = j === 2 ? 0.2 : 0.38;
            this.setRibbon(s, j, true, x - ux * 0.25, wy + 0.02, z - uz * 0.25,
              -ux * L + px * sd * L * lw, -uz * L + pz * sd * L * lw, (H * hj) / 0.66, j === 2 ? 0.13 : 0.2, Math.min(1, 1.3 * k) * 0.95);
          }
          const nd = this.take(A, (60 + 5 * hs) * k * m, dt);
          for (let i = 0; i < nd; i++) {
            const back = rnd(-0.3, 0.9), sd = (Math.random() < 0.5 ? -1 : 1) * rnd(0.12, 0.55);
            const up = rnd(6, 13.5) * (0.55 + 0.45 * sk) * (0.65 + 0.35 * sp);
            const sz = rnd(0.08, 0.15) + 0.06 * k * Math.random();
            const st = Math.random() < 0.4;
            this.emit(st ? WK.Streak : WK.Drop, x - ux * back + px * sd, wy + 0.08, z - uz * back + pz * sd,
              ux * hs * rnd(0.6, 0.9) + px * sd * 5, up, uz * hs * rnd(0.6, 0.9) + pz * sd * 5, st ? 0.7 : 2.2, 0, 16, 1.3, wy, st ? sz * 0.8 : sz, sz * 0.7, 1, 1);
          }
          // the fan: two lobes of lit spray rising 2-3 m behind and beside the rider and peeling
          // out into the V, leaving the line between the chase camera and the rider clear
          const np = this.take(A + 1, 16 * k * m, dt);
          for (let i = 0; i < np; i++) {
            const sd = Math.random() < 0.5 ? -1 : 1, lat = rnd(0.35, 0.9), out = rnd(2, 4.5);
            const back = rnd(0, 0.8);
            this.emit(WK.Puff, x - ux * back + px * sd * lat, wy + rnd(0.1, 0.4), z - uz * back + pz * sd * lat,
              ux * hs * rnd(0.72, 0.92) + px * sd * out, rnd(6, 10) * sk, uz * hs * rnd(0.72, 0.92) + pz * sd * out,
              rnd(0.7, 1.0), 0, 5.5, 0.7, wy - 0.3, 0.3 + 0.15 * k, 1.0 + 0.9 * k, 1, 0.8);
          }
          // V wings thrown off both sides
          const nw = this.take(A + 2, 60 * k * m, dt);
          for (let i = 0; i < nw; i++) {
            const sd = Math.random() < 0.5 ? -1 : 1, o = rnd(3, 6.5) * (0.5 + 0.5 * sp);
            const fw = rnd(-0.3, 0.6);
            const st = Math.random() < 0.3;
            this.emit(st ? WK.Streak : WK.Drop, x + ux * fw + px * sd * 0.35, wy + 0.06, z + uz * fw + pz * sd * 0.35,
              px * sd * o + ux * hs * rnd(0.55, 0.8), rnd(2.5, 5.5) * (0.5 + 0.5 * sk), pz * sd * o + uz * hs * rnd(0.55, 0.8), 1.4, 0, 16, 1.1, wy,
              st ? 0.06 : rnd(0.07, 0.13), 0.05, 1, 0.9);
          }
          // a foam trail and the wake (rings dropped along the path open into a V)
          this.tDist[s] += moved;
          if (sk > 0.35 && this.tDist[s] > 0.55) {
            this.tDist[s] = 0;
            this.emit(WK.Foam, x - ux * 0.5, wy, z - uz * 0.5, 0, 0, 0, rnd(1.6, 2.4), 0, 0, 1, 0, 0.35 * sk + 0.15, 0.7 + 0.8 * k, 0.95 * sk + 0.05, 0.6, ux * 1.8, uz * 1.8);
          }
          this.tDist2[s] += moved;
          if (sk > 0.2 && this.tDist2[s] > 1.3) {
            this.tDist2[s] = 0;
            this.emit(WK.Ring, x - ux * 0.4, wy, z - uz * 0.4, 0, 0, 0, 1.5, 0, 0, 1, 0, 0.5, 2.2 + 2.5 * k, 0.9 * sk, 0, ux * 1.3, uz * 1.3);
            const ls = 1.5 + hs * 0.08;
            for (let sd = -1; sd <= 1; sd += 2) {
              this.emit(WK.Foam, x + px * sd * 0.5, wy, z + pz * sd * 0.5, px * sd * ls, 0, pz * sd * ls, rnd(1.4, 2), 0, 0, 1.1, 0, 0.25, 0.6 + 0.4 * k, 0.8 * sk, 0.4, ux * 2.2, uz * 2.2);
            }
          }
        }
        // ---- the jet pushing on the water: a dimple, a spray corona, rings
        const hH = flight ? SKIM_H * 1.25 : HOVER_H;
        const close = above < hH ? Math.pow(1 - above / hH, 1.3) : 0;
        const pw = Math.min(1, thr * (flight ? 1 : 1.8)) * close;
        if (pw > 0.04) {
          dimpleOn = true;
          const back = flight ? 0.5 + above * 0.55 : 0.12;
          const bx = Math.sin(yaw), bz = Math.cos(yaw);
          const dx = x + (flight ? -ux : bx) * back, dz = z + (flight ? -uz : bz) * back;
          const rad = (flight ? 0.75 : 0.65) + above * 0.22;
          const el = flight ? 1 + Math.min(1.3, hs / 14) : 1;
          this.setDimple(s, true, dx, wy, dz, rad, Math.min(1, pw * 1.4), ux * el, uz * el);
          if (!flight) {
            // hover: the circular spray corona under the nozzles
            const nd = this.take(A + 4, 150 * pw * m, dt);
            for (let i = 0; i < nd; i++) {
              const a = Math.random() * 6.2832, ca = Math.cos(a), sa = Math.sin(a);
              const r0 = rad * rnd(0.5, 0.75), o = rnd(2.5, 6) * (0.4 + pw) + 0.8;
              this.emit(Math.random() < 0.3 ? WK.Streak : WK.Drop, dx + ca * r0, wy + 0.05, dz + sa * r0, ca * o, rnd(1, 3.2) * (0.4 + pw) + 0.4, sa * o,
                1.2, 0, 18, 0.8, wy, rnd(0.05, 0.09), 0.04, 1, 0.9);
            }
            const nm = this.take(A + 5, 30 * pw * m, dt);
            for (let i = 0; i < nm; i++) {
              const a = Math.random() * 6.2832, ca = Math.cos(a), sa = Math.sin(a), o = rnd(1.8, 3.6);
              this.emit(WK.Puff, dx + ca * rad * 0.7, wy + 0.15, dz + sa * rad * 0.7, ca * o, rnd(0.2, 1.0), sa * o, rnd(0.8, 1.2), 0, 0.6, 1.8, -1e5,
                0.25, 0.75 + 0.5 * pw, 0.9, 0.6);
            }
            const nr = this.take(A + 6, 3.2 * pw + 0.6, dt);
            for (let i = 0; i < nr; i++) this.emit(WK.Ring, dx, wy, dz, 0, 0, 0, 1.1, 0, 0, 1, 0, rad * 0.9, rad + 1.6 + 1.6 * pw, 0.95, 0);
          }
        }
      }
    }
    if (!dimpleOn) this.setDimple(s, false, 0, 0, 0, 0, 0, 0, 0);
    if (!ribOn) this.ribbonsOff(s);

    // ---- waterfalls
    const pf = this.tFall[s];
    if (fall > 0 && pf <= 0) { this.fallBurst(x, y, z, vx, vy, vz, Math.min(1, Math.hypot(hs, vy) / 20)); ev |= 4; this.evPos.x = x; this.evPos.y = y + 1; this.evPos.z = z; }
    else if (fall <= 0 && pf > 0) { this.fallBurst(x, y, z, vx, vy, vz, 1); this.tDrip[s] = 1; }
    if (fall > 0) {
      this.tWet[s] = 0;
      const n = this.take(A + 10, 38 * fall * m, dt);
      const nm = this.take(A + 5, 7 * fall * m, dt);
      for (let i = 0; i < nm; i++) {
        // the sheet breaking over the hat and shoulders
        const a = Math.random() * 6.2832;
        this.emit(WK.Puff, x + Math.cos(a) * 0.35, y + rnd(1.3, 1.9), z + Math.sin(a) * 0.35, Math.cos(a) * rnd(1, 2.5) + vx * 0.5, rnd(-1, 0.5), Math.sin(a) * rnd(1, 2.5) + vz * 0.5,
          rnd(0.55, 0.9), 0, 3, 2, -1e5, 0.3, 1.0, 0.95, 0.6);
      }
      for (let i = 0; i < n; i++) {
        const a = Math.random() * 6.2832, o = rnd(1.5, 4);
        this.emit(WK.Drop, x + Math.cos(a) * 0.3, y + rnd(1.2, 1.8), z + Math.sin(a) * 0.3, Math.cos(a) * o + vx * 0.6, rnd(0, 1.5), Math.sin(a) * o + vz * 0.6,
          0.9, 0, 22, 0.6, -1e5, rnd(0.07, 0.12), 0.05, 1, 0.9);
      }
    }
    // ---- drips for a second after leaving the water
    if (!(wet && above <= 0.35) && fall <= 0) {
      const w0 = this.tWet[s];
      if (w0 < 1.3) {
        const nd = this.take(A + 7, (pf > 0 || this.tDrip[s] > 0 ? 34 : 16) * (1 - w0 / 1.3) * m, dt);
        for (let i = 0; i < nd; i++) {
          this.emit(WK.Drop, x + rnd(-0.3, 0.3), y + rnd(0.3, 1.8), z + rnd(-0.3, 0.3), vx * 0.8, vy * 0.8 - rnd(0, 1.5), vz * 0.8, 0.6, 0, 22, 0.4, -1e5,
            rnd(0.06, 0.1), 0.04, 0.95, 0.7);
        }
      }
      this.tWet[s] = w0 + dt;
      if (w0 >= 1.3) this.tDrip[s] = 0;
    }

    // ---- cooling steam off the pack (rate mirrors the sim; amount = what there is to cool)
    this.own = 0; // the steam is the cooling read: it must show from the chase camera
    let cool = 0;
    if (wet) {
      if (above < 0.35) cool = WADE_COOL;
      else if (!grounded && above < SKIM_H) cool = SKIM_COOL * (1 - above / SKIM_H);
    }
    if (fall > 0) cool = Math.max(cool, FALL_COOL * fall);
    const gate = heat > 0.005 ? 0.25 + 0.75 * clamp01(heat * 2.5) : 0.12;
    const amt = Math.min(1.4, cool / 62) * gate;
    if (cool > 0 && this.tCool[s] <= 0 && heat > 0.15) {
      ev |= 2; this.lastCool = heat; this.evPos.x = x; this.evPos.y = y + 1.2; this.evPos.z = z;
    }
    this.tCool[s] = cool;
    if (amt > 0.01) {
      const bx = Math.sin(yaw), bz = Math.cos(yaw);
      const flight = move === 3;
      const sx = x + bx * 0.28, sy = y + (flight ? 1.0 : 1.3), sz = z + bz * 0.28;
      const burst = (ev & 2) !== 0;
      const n = this.take(A + 8, 34 * amt * m, dt) + (burst ? 10 : 0);
      for (let i = 0; i < n; i++) {
        this.emit(WK.Steam, sx + rnd(-0.15, 0.15), sy + rnd(-0.1, 0.2), sz + rnd(-0.15, 0.15),
          vx * 0.5 + bx * rnd(0.3, 1) + rnd(-0.4, 0.4), rnd(1.2, 2.6) + vy * 0.3, vz * 0.5 + bz * rnd(0.3, 1) + rnd(-0.4, 0.4),
          rnd(0.9, 1.4), 0, -1.2, 2.0, -1e5, 0.18, 0.6 + 0.35 * amt, Math.min(0.95, 0.55 + 0.4 * amt), 0.2);
      }
      if (heat > 0.08) {
        const nh = this.take(A + 9, 22 * amt * m, dt) + (burst ? 10 : 0);
        for (let i = 0; i < nh; i++) {
          // hiss jets out of the side vents
          const sd = Math.random() < 0.5 ? -1 : 1, o = rnd(3, 6.5);
          const lx = Math.cos(yaw) * sd, lz = -Math.sin(yaw) * sd;
          this.emit(WK.Streak, sx + lx * 0.18, sy + rnd(-0.05, 0.25), sz + lz * 0.18, lx * o + vx * 0.7, rnd(0.8, 2.6) + vy * 0.5, lz * o + vz * 0.7,
            rnd(0.18, 0.3), 0, -2, 3, -1e5, 0.035, 0.05, 0.9, 0);
        }
      }
    }

    this.tAbove[s] = above; this.tVy[s] = vy; this.tFall[s] = fall;
    this.own = 0;
    return ev;
  }
}
